import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchIcon } from '../../assets/icons';
import { IInputProps, Input } from '../Input';

export interface ISearchInputProps extends Omit<IInputProps, 'value' | 'onChange' | 'suffix' | 'type'> {
    /** The committed search term. */
    value: string;
    /** Called with the term once the user stops typing. */
    onChange: (value: string) => void;
    debounceMs?: number;
}

/**
 * Text input for full text search over a listing. Keeps what the user types locally
 * and only reports the term upwards after `debounceMs`, so it can drive a query directly.
 */
export const SearchInput = ({ value, onChange, debounceMs = 300, ...inputProps }: ISearchInputProps) => {
    const [innerValue, setInnerValue] = useState(value);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    // Follow the committed value when it is changed from the outside (e.g. cleared with filters)
    useEffect(() => {
        setInnerValue((prevValue) => (prevValue === value ? prevValue : value));
    }, [value]);

    useEffect(() => {
        if (innerValue === value) return;

        const timeout = setTimeout(() => onChangeRef.current(innerValue), debounceMs);
        return () => clearTimeout(timeout);
    }, [innerValue, value, debounceMs]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setInnerValue(event.target.value);
    }, []);

    return (
        <Input
            {...inputProps}
            type="search"
            value={innerValue}
            onChange={handleChange}
            suffix={<SearchIcon color="var(--color-gray-600)" />}
        />
    );
};
